import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaDraftingCompass } from '@react-icons/all-files/fa/FaDraftingCompass';
import { FaProjectDiagram }  from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }             from '@react-icons/all-files/fa/FaCog';
import { FaHome }            from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, updateDiseno, reset } from '../../../../manager/workflow-diseno.reducer';

const BACK = '/configuracion/workflow/workflow-diseno';

interface DisenoForm {
  id:     string;
  nombre: string;
  activo: string;
}

const empty: DisenoForm = { id: '', nombre: '', activo: 'true' };

const fields: FormField<DisenoForm>[] = [
  {
    name:     'id',
    label:    'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name:        'nombre',
    label:       'Nombre',
    required:    true,
    placeholder: 'Nombre del diseño',
  },
  {
    name:  'activo',
    label: 'Activo',
    type:  'checkbox',
  },
];

export default function WorkflowDisenoFormPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity        = useAppSelector(s => s.workflowDiseno.entity) as any;
  const updating      = useAppSelector(s => s.workflowDiseno.updating);
  const updateSuccess = useAppSelector(s => s.workflowDiseno.updateSuccess);

  const [form,   setForm]   = useState<DisenoForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof DisenoForm, string>>>({});

  useEffect(() => {
    if (id) {
      dispatch(getEntity(id));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (entity?.id) {
      setForm({
        id:     String(entity.id),
        nombre: entity.nombre ?? '',
        activo: String(entity.activo ?? true),
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: { successMsg: 'Diseño actualizado correctamente.' },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (name: keyof DisenoForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const e2: Partial<Record<keyof DisenoForm, string>> = {};
    if (!form.nombre.trim()) e2.nombre = 'El nombre es requerido';
    if (Object.keys(e2).length) { setErrors(e2); return; }

    dispatch(updateDiseno({
      ...entity,
      id:     Number(id),
      nombre: form.nombre,
      activo: form.activo === 'true',
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio',          to: '/',                       icon: <FaHome /> },
        { label: 'Configuración',   to: '/configuracion',          icon: <FaCog /> },
        { label: 'Workflow',        to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Workflow Diseño', to: BACK,                      icon: <FaDraftingCompass /> },
        { label: `Editar Diseño #${id}` },
      ]} />

      <PageHeader
        icon={<FaDraftingCompass />}
        iconColor="#ec4899"
        title="Editar Diseño"
        subtitle={`Modificando diseño #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={false}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#ec4899"
      />
    </div>
  );
}
